export interface FormElements {
  name: HTMLInputElement;
  email: HTMLInputElement;
  phone: HTMLInputElement;
  reservationdate: HTMLInputElement;
  reservationtime: HTMLInputElement;
  numberofpersons: HTMLInputElement;
  remarks: HTMLTextAreaElement;
}

const startHolidayStamp = "2026-08-09";
const endHolidayStamp = "2026-08-24";

const startHoliday = new Date(startHolidayStamp);
const endHoliday = new Date(endHolidayStamp);
const showHolidayDialogEndDate = new Date(`${endHolidayStamp}T23:59:59`);

/*
 * Form validations
 */
const reservationForm = document.getElementById(
  "reservationForm",
) as HTMLFormElement;
const formMessage = document.getElementById("formMessage");
const reservationButton = document.getElementById(
  "reservationButton",
) as HTMLButtonElement;
const backToFormButton = document.getElementById(
  "backToFormButton",
) as HTMLButtonElement;
const formMessageContent = document.getElementById("formMessageContent");

const elements = reservationForm.elements;

const formElements: FormElements = {
  name: elements.namedItem("name") as HTMLInputElement,
  email: elements.namedItem("email") as HTMLInputElement,
  phone: elements.namedItem("phone") as HTMLInputElement,
  reservationdate: elements.namedItem("reservationdate") as HTMLInputElement,
  reservationtime: elements.namedItem("reservationtime") as HTMLInputElement,
  numberofpersons: elements.namedItem("numberofpersons") as HTMLInputElement,
  remarks: elements.namedItem("remarks") as HTMLTextAreaElement,
};

// set default min date for reservation date input formElements.reservationdate to today
const today = new Date().toISOString().split("T")[0];
formElements.reservationdate.min = today;

const hideFormAndShowMessage = () => {
  // hide form
  reservationForm?.classList.add("hidden");

  // show formMessage container and scroll to it
  formMessage?.classList.remove("hidden");
  formMessage?.scrollIntoView({ behavior: "smooth" });
};

const showFormAndHideMessage = () => {
  // show form and scroll to it
  reservationForm?.classList.remove("hidden");
  reservationForm?.scrollIntoView({ behavior: "smooth" });

  // reset submit button
  reservationButton.textContent = "Versturen";
  reservationButton.disabled = false;

  // hide formMessage container
  formMessage?.classList.add("hidden");
};

const showFormErrorMessage = () => {
  if (!formMessageContent) return;

  formMessageContent.innerHTML = `
  <h2>Er is iets misgegaan</h2>
  <p>Er is iets misgegaan bij het versturen van je reservering. Probeer het later opnieuw of neem direct contact met ons op via:</p>
  <p><a href="mailto:info@vinote.nl">info@vinote.nl</a><br /><a href="tel:+31108998060" rel="noreferrer"
    >+31 (0)10 899 80 60</a
  ></p>`;

  hideFormAndShowMessage();
};

const showFormSuccessMessage = () => {
  if (!formMessageContent) return;

  formMessageContent.innerHTML = `
    <h2>Bedankt voor je reservering</h2>
    <p>We hebben je reservering ontvangen en zullen er zo snel mogelijk op reageren.</p>
    <p>Een reservering is pas geaccepteerd zodra je een bevestiging hebt ontvangen.</p>`;

  reservationForm.reset();

  hideFormAndShowMessage();
};

const hasInvalidReservationDate = (date: string) => {
  const selectedDate = new Date(date);
  const day = selectedDate.getDay();
  return day === 0 || day === 1; // 0 = Sunday, 1 = Monday
};

const hasEarlyClosingTime = (date: string, time: string) => {
  const selectedDate = new Date(date);
  const day = selectedDate.getDay();
  const hour = parseInt(time.split(":")[0], 10);
  return (day === 2 || day === 3) && hour >= 15;
};

const hasSpecialClosingTime = (date: string, time: string) => {
  const hour = parseInt(time.split(":")[0], 10);
  return date === "2026-05-14" && hour >= 15;
};

const isHoliday = (date: string) => {
  const inputDate = new Date(date);

  return inputDate >= startHoliday && inputDate <= endHoliday;
};

export const getHolidayMessage = (startHoliday: Date, endHoliday: Date) => {
  const sameMonth = startHoliday.getMonth() === endHoliday.getMonth();

   const startHolidayDay = new Intl.DateTimeFormat("nl-NL", {
      weekday: "long",
      day: "numeric",
    }).format(startHoliday);
    const endHolidayDay = new Intl.DateTimeFormat("nl-NL", {
      weekday: "long",
      day: "numeric",
    }).format(endHoliday);
    const endHolidayMonth = new Intl.DateTimeFormat("nl-NL", {
      month: "long",
    }).format(endHoliday);
    const startHolidayLabel = sameMonth
      ? startHolidayDay
      : `${startHolidayDay} ${new Intl.DateTimeFormat("nl-NL", {
          month: "long",
        }).format(startHoliday)}`;

    return `Van ${startHolidayLabel} tot en met ${endHolidayDay} ${endHolidayMonth} zijn wij wegens vakantie gesloten.`;
};

backToFormButton.addEventListener("click", () => {
  showFormAndHideMessage();
});

reservationForm.addEventListener("onload", () => {
  const today = new Date().toISOString().split("T")[0];
  reservationForm.reservationdate?.setAttribute("min", today);
});

reservationButton.addEventListener("click", () => {
  reservationValidationCheck(formElements);
});

reservationForm.addEventListener("submit", (event) => {
  event.preventDefault();

  reservationButton.disabled = true;
  reservationButton.textContent = "Bezig met versturen...";

  // submit data
  const formData = new FormData();
  formData.append("submitReservation", "true");
  formData.append("name", formElements.name.value);
  formData.append("email", formElements.email.value);
  formData.append("phone", formElements.phone.value);
  formData.append("reservationdate", formElements.reservationdate.value);
  formData.append("reservationtime", formElements.reservationtime.value);
  formData.append("numberofpersons", formElements.numberofpersons.value);
  formData.append("remarks", formElements.remarks.value);

  fetch("https://www.vinote.nl/api/reservations.php", {
    method: "POST",
    body: formData,
  })
    .then((response) => response.json())
    .then((data) => {
      if (data.success) {
        showFormSuccessMessage();
      } else {
        showFormErrorMessage();
      }
    })
    .catch(() => {
      showFormErrorMessage();
    });
});

export const reservationValidationCheck = (formElements: FormElements) => {
  if (formElements.name.value === "") {
    formElements.name.setCustomValidity(
      "We zijn benieuwd wie er bij ons komt eten!",
    );
  } else {
    formElements.name.setCustomValidity("");
  }

  const emailValidityState = formElements.email.validity;
  const reservationtimeValidityState = formElements.reservationtime.validity;
  const date = formElements.reservationdate.value;
  const time = formElements.reservationtime.value;

  if (formElements.email.value === "" || emailValidityState.typeMismatch) {
    formElements.email.setCustomValidity(
      "We hebben een geldig e-mailadres nodig om de reservering te bevestigen.",
    );
  } else {
    formElements.email.setCustomValidity("");
  }

  if (formElements.numberofpersons.value === "") {
    formElements.numberofpersons.setCustomValidity(
      "Vertel ons met hoeveel personen je komt.",
    );
  } else {
    formElements.numberofpersons.setCustomValidity("");
  }

  if (formElements.reservationdate.value === "") {
    formElements.reservationdate.setCustomValidity(
      "Wanneer wil je bij ons reserveren?",
    );
  } else if (isHoliday(date)) {
    const holidayMessage = getHolidayMessage(startHoliday, endHoliday);
    formElements.reservationdate.setCustomValidity(holidayMessage);
  } else if (hasInvalidReservationDate(date)) {
    formElements.reservationdate.setCustomValidity(
      "Op zondag en maandag zijn wij gesloten.",
    );
  } else {
    formElements.reservationdate.setCustomValidity("");
  }

  if (formElements.reservationtime.value === "") {
    formElements.reservationtime.setCustomValidity(
      "Hoe laat wil je bij ons reserveren?",
    );
  } else if (hasEarlyClosingTime(date, time)) {
    formElements.reservationtime.setCustomValidity(
      "Op dinsdag en woensdag sluiten wij om 15:30 uur.",
    );
  } else if (hasSpecialClosingTime(date, time)) {
    formElements.reservationtime.setCustomValidity(
      "Op donderdag 14 mei 2026 zijn wij geopend van 10:00 uur tot 15:30 uur.",
    );
  } else if (reservationtimeValidityState.rangeOverflow) {
    formElements.reservationtime.setCustomValidity(
      "Reserveren is mogelijk tot uiterlijk 19:00 uur, we sluiten om 22:00 uur.",
    );
  } else {
    formElements.reservationtime.setCustomValidity("");
  }

  if (formElements.remarks.value === "") {
    formElements.remarks.setCustomValidity(
      "Vertel ons meer over je reservering.",
    );
  } else {
    formElements.remarks.setCustomValidity("");
  }
};

/*
 * Dialog
 */
const dialog = document.querySelector("dialog");
const continueButton = document.querySelector("#dialogContinueButton");
const holidayMessageContainer = document.querySelector("#holidayMessageContainer");
const holidayMessage = getHolidayMessage(startHoliday, endHoliday);

const currentDate = new Date();

if (currentDate < showHolidayDialogEndDate && holidayMessageContainer) {
  holidayMessageContainer.innerHTML = holidayMessage;
  dialog?.showModal();
}

continueButton?.addEventListener("click", () => {
  dialog?.close();
});
